import React from 'react';
import moment from 'moment';
import { connect } from 'react-redux';
import Select from 'react-select';
import { DatePicker } from 'antd';
import { Field, getFormSubmitErrors, reduxForm, SubmissionError, change } from 'redux-form';
import { Link } from 'react-router-dom';
import { Button } from '@kambria/kambria-lego';
import { IntlPhoneInput, PublicLink } from 'views/components/core/ReduxForm.js';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import Modal from 'react-bootstrap4-modal';
import client from 'helpers/client';
import forgetPasswordImage from 'static/images/mvp/forget-password.png';
import {
  checkPublicUsername,
  fetchAllReferences,
  updateUserInfo,
  fetchUserMvp,
  getUserProfileMvp,
  logOutMvp,
} from 'redux/actions/user.action';
import { getCategorySessions } from 'redux/actions/mvp/session.action';
import utils from 'helpers/utils';
import Utils from '../../../../helpers/utils';
import constants from 'helpers/const';
import { getDaoDetailByCode } from 'redux/actions/mvp/ced-package.action.js';
import Web3 from 'web3';
import { I18n } from '../../../../helpers/i18n';
import { testProfileReload, clearAllFlags, monitorComponentLifecycle } from '../../../../helpers/testProfileReload';

const FORM_NAME = 'formUserProfile';
const { USER_EXPERIENCES } = constants;

const formatExperienceForSelect = (experience) => {
  if (experience) {
    return {
      label: utils.getUserExperienceLabel(experience),
      value: experience,
    };
  }
};

const formatValueForMultiSelect = (data) => {
  return (data && data.map((value) => ({ label: value, value }))) || [];
};

const formatValueForSelect = (value) => {
  return (value && { label: value, value }) || '';
};

const customSelectStyles = {
  control: (_, { selectProps: { isDisabled } }) => {
    return {
      background: isDisabled ? '#e9ecef' : '#FFF',
      borderRadius: 8,
      minHeight: 46,
      display: 'flex',
      border: '1px solid #cfcfcf',
    };
  },
};

const INPUT_FIELDS = ['firstName', 'dateOfBirth', 'phoneNumber'];
const SELECT_FIELDS = [];
const MULTI_SELECT_FIELDS = ['favouriteTopics', 'sharingTopics'];

class Profile extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      completePercent: 0,
      showDeleteModal: false,
      isDeletingAccount: false,
      categories: [],
    };
    this._isMounted = true;
    // Use ref to prevent race condition with setState (which is async)
    this._isDeletingAccountRef = false;
    this.handleClickOutside = this.handleClickOutside.bind(this);
    this._selectControlListenerSetup = false; // Flag to prevent duplicate listener setup
  }

  async componentDidMount() {
    // Test for reload issues
    testProfileReload();
    
    // Only fetch references once
    const { fetchAllReferences, dispatch, user } = this.props;
    fetchAllReferences();
    
    // Initialize favouriteTopics as empty array immediately if not already set
    // This ensures the select component works even before categories are loaded
    if (user && (!user.favouriteTopics || !Array.isArray(user.favouriteTopics) || user.favouriteTopics.length === 0)) {
      dispatch(change(FORM_NAME, 'favouriteTopics', []));
    }
    
    // Initialize sharingTopics as empty array immediately if not already set
    if (user && (!user.sharingTopics || !Array.isArray(user.sharingTopics) || user.sharingTopics.length === 0)) {
      dispatch(change(FORM_NAME, 'sharingTopics', []));
    }
    
    // Fetch categories for favourite topics and sharing topics
    await this.fetchCategories();
    
    // Update favouriteTopics and sharingTopics in form after initial load
    if (this._isMounted) {
      setTimeout(() => {
        if (this._isMounted) {
          this.updateFavouriteTopicsInForm();
          this.updateSharingTopicsInForm();
        }
      }, 200);
    }
    
    // Add click outside listener for dropdown
    // Use 'click' instead of 'mousedown' to avoid interfering with react-select's mousedown handler
    document.addEventListener('click', this.handleClickOutside);
    
    // Add event listener for select control click
    // Use setTimeout to ensure select is rendered
    if (!this._selectControlListenerSetup) {
      setTimeout(() => {
        if (this._isMounted && !this._selectControlListenerSetup) {
          this.setupSelectControlClickListener();
        }
      }, 500);
    }
  }

  componentDidUpdate(prevProps, prevState) {
    // Refetch categories when language changes
    const prevLanguage = prevProps.language || localStorage.getItem('ced:language') || 'vi';
    const currentLanguage = this.props.language || localStorage.getItem('ced:language') || 'vi';
    if (prevLanguage !== currentLanguage && this._isMounted) {
      this.fetchCategories();
    }
    
    // Update favouriteTopics and sharingTopics when categories are loaded or user data changes
    const categoriesJustLoaded = prevState.categories.length === 0 && this.state.categories.length > 0;
    const userFavouriteTopicsChanged = prevProps.user?.favouriteTopics !== this.props.user?.favouriteTopics;
    const userSharingTopicsChanged = prevProps.user?.sharingTopics !== this.props.user?.sharingTopics;
    const userChanged = prevProps.user?._id !== this.props.user?._id;
    
    if (this._isMounted && this.state.categories.length > 0 && 
        (categoriesJustLoaded || userFavouriteTopicsChanged || userSharingTopicsChanged || userChanged)) {
      // Small delay to ensure state is updated
      setTimeout(() => {
        if (this._isMounted) {
          this.updateFavouriteTopicsInForm();
          this.updateSharingTopicsInForm();
        }
      }, 100);
    }
  }

  fetchCategories = async () => {
    try {
      const { getCategorySessions } = this.props;
      const categories = await getCategorySessions();
      if (categories && Array.isArray(categories) && this._isMounted) {
        this.setState({ categories }, () => {
          // Update form with favouriteTopics and sharingTopics after categories are loaded
          if (this._isMounted) {
            this.updateFavouriteTopicsInForm();
            this.updateSharingTopicsInForm();
          }
        });
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  }

  updateFavouriteTopicsInForm = () => {
    const { user, dispatch } = this.props;
    
    if (user && user.favouriteTopics && Array.isArray(user.favouriteTopics) && this.state.categories.length > 0) {
      const favouriteTopicsOptions = user.favouriteTopics.map(topicId => {
        // Handle both ObjectId and string formats
        const topicIdStr = topicId?.toString() || topicId;
        const category = this.findCategoryById(topicIdStr);
        if (category) {
          return {
            value: category._id,
            label: category.name
          };
        }
        return null;
      }).filter(Boolean);
      
      if (favouriteTopicsOptions.length > 0) {
        dispatch(change(FORM_NAME, 'favouriteTopics', favouriteTopicsOptions));
      } else {
        // Ensure it's always an array, even if empty
        dispatch(change(FORM_NAME, 'favouriteTopics', []));
      }
    } else if (user && (!user.favouriteTopics || (Array.isArray(user.favouriteTopics) && user.favouriteTopics.length === 0))) {
      // Clear the field if user has no favouriteTopics - ensure it's always an array
      dispatch(change(FORM_NAME, 'favouriteTopics', []));
    } else if (user && this.state.categories.length === 0) {
      // If categories not loaded yet but user exists, ensure favouriteTopics is initialized as empty array
      // This allows the select to work even before categories are loaded
      dispatch(change(FORM_NAME, 'favouriteTopics', []));
    }
  }

  updateSharingTopicsInForm = () => {
    const { user, dispatch } = this.props;
    
    if (user && user.sharingTopics && Array.isArray(user.sharingTopics) && this.state.categories.length > 0) {
      // Get all available sharing category options
      const allSharingOptions = this.getSharingCategoryOptions();
      
      // sharingTopics contains pairs of IDs: [level1Id1, level2Id1, level1Id2, level2Id2, ...]
      // Pair them up and find matching options from the options array
      const sharingTopicsOptions = [];
      
      // Process IDs in pairs (every two IDs form a pair)
      for (let i = 0; i < user.sharingTopics.length; i += 2) {
        if (i + 1 < user.sharingTopics.length) {
          const level1Id = user.sharingTopics[i];
          const level2Id = user.sharingTopics[i + 1];
          
          // Convert to strings for comparison
          const level1IdStr = level1Id?.toString() || level1Id;
          const level2IdStr = level2Id?.toString() || level2Id;
          
          // Find matching option from allSharingOptions by comparing level1Id and level2Id
          const matchingOption = allSharingOptions.find(option => {
            const optLevel1IdStr = option.level1Id?.toString() || option.level1Id;
            const optLevel2IdStr = option.level2Id?.toString() || option.level2Id;
            return optLevel1IdStr === level1IdStr && optLevel2IdStr === level2IdStr;
          });
          
          if (matchingOption) {
            sharingTopicsOptions.push(matchingOption);
          }
        }
      }
      
      // Always update form, even if empty array (to ensure form state is correct)
      dispatch(change(FORM_NAME, 'sharingTopics', sharingTopicsOptions));
    } else if (user && (!user.sharingTopics || (Array.isArray(user.sharingTopics) && user.sharingTopics.length === 0))) {
      // Clear the field if user has no sharingTopics - ensure it's always an array
      dispatch(change(FORM_NAME, 'sharingTopics', []));
    } else if (user && user.sharingTopics && Array.isArray(user.sharingTopics) && this.state.categories.length === 0) {
      // If categories not loaded yet but user has sharingTopics, fetch categories first
      this.fetchCategories().then(() => {
        if (this._isMounted) {
          setTimeout(() => {
            this.updateSharingTopicsInForm();
          }, 100);
        }
      });
    } else if (user && this.state.categories.length === 0) {
      // If categories not loaded yet but user exists, ensure sharingTopics is initialized as empty array
      // This allows the select to work even before categories are loaded
      dispatch(change(FORM_NAME, 'sharingTopics', []));
    }
  }

  findCategoryById = (categoryId) => {
    // Search in level 1 categories
    let category = this.state.categories.find(cat => {
      const catId = cat._id?.toString() || cat._id;
      return catId === categoryId;
    });
    
    // If not found in level 1, search in level 2 (children)
    if (!category) {
      for (const level1Cat of this.state.categories) {
        if (level1Cat.children && Array.isArray(level1Cat.children)) {
          category = level1Cat.children.find(child => {
            const childId = child._id?.toString() || child._id;
            return childId === categoryId;
          });
          if (category) break;
        }
      }
    }
    
    return category;
  }

  getAllCategoryOptions = () => {
    // Only include level 1 categories (not level 2)
    const options = [];
    
    this.state.categories.forEach(level1Cat => {
      // Add level 1 category only
      options.push({
        value: level1Cat._id,
        label: level1Cat.name
      });
    });
    
    return options;
  }

  getSharingCategoryOptions = () => {
    // Return level 2 categories with format "Level 1 > Level 2"
    // Use string representation for value so react-select can compare properly
    const options = [];
    
    this.state.categories.forEach(level1Cat => {
      if (level1Cat.children && Array.isArray(level1Cat.children) && level1Cat.children.length > 0) {
        level1Cat.children.forEach(level2Cat => {
          const level1IdStr = level1Cat._id?.toString() || level1Cat._id;
          const level2IdStr = level2Cat._id?.toString() || level2Cat._id;
          options.push({
            value: `${level1IdStr}_${level2IdStr}`, // String representation for react-select comparison
            level1Id: level1Cat._id,
            level2Id: level2Cat._id,
            label: `${level1Cat.name} > ${level2Cat.name}`
          });
        });
      }
    });
    
    return options;
  }

  handleClickOutside = (event) => {
    // First, check if any menu is currently open
    const openMenuControl = document.querySelector('.select__control.select__control--menu-is-open');
    if (!openMenuControl) {
      // No menu is open, don't interfere with opening menus
      return;
    }
    
    // Save reference before setTimeout
    const clickTarget = event.target;
    
    // Use setTimeout to let react-select handle the click first
    setTimeout(() => {
      // Check if click is outside the dropdown menu (css-1bfbmi5)
      const dropdownMenu = clickTarget.closest('.css-1bfbmi5');
      
      // Check if click is on menu list or menu items
      let isClickOnMenu = false;
      let currentElement = clickTarget;
      
      // First check: Check if element has id containing "react-select" and "option" (menu items)
      const elementId = clickTarget.id || '';
      if (typeof elementId === 'string' && elementId.includes('react-select') && elementId.includes('option')) {
        isClickOnMenu = true;
      }
      
      // Second check: Traverse up to find menu-related classes
      if (!isClickOnMenu) {
        while (currentElement && currentElement !== document.body) {
          const className = currentElement.className || '';
          const elementId = currentElement.id || '';
          
          if (typeof className === 'string') {
            // Check for menu-related classes
            if (className.includes('select__menu') || 
                className.includes('select__option') || 
                className.includes('menu-list') ||
                className === 'css-1bfbmi5') {
              isClickOnMenu = true;
              break;
            }
          }
          
          // Also check ID for menu items
          if (typeof elementId === 'string' && elementId.includes('react-select') && elementId.includes('option')) {
            isClickOnMenu = true;
            break;
          }
          
          currentElement = currentElement.parentElement;
        }
      }
      
      // Third check: Check if element is inside menu list using closest
      if (!isClickOnMenu) {
        const menuList = clickTarget.closest('.select__menu-list');
        if (menuList) {
          isClickOnMenu = true;
        }
      }
      
      // Fourth check: Check using closest for menu portal
      if (!isClickOnMenu) {
        const menuPortal = clickTarget.closest('[class*="menu"]');
        if (menuPortal && menuPortal.className && typeof menuPortal.className === 'string' && 
            (menuPortal.className.includes('select__menu') || menuPortal.className.includes('menu-list'))) {
          isClickOnMenu = true;
        }
      }
      
      // Fifth check: Check if element is inside any element with id containing "react-select" and "option"
      if (!isClickOnMenu) {
        let parent = clickTarget.parentElement;
        while (parent && parent !== document.body) {
          const parentId = parent.id || '';
          if (typeof parentId === 'string' && parentId.includes('react-select') && parentId.includes('option')) {
            isClickOnMenu = true;
            break;
          }
          parent = parent.parentElement;
        }
      }
      
      // Find the select control element that is focused and has menu open
      const selectControl = document.querySelector('.select__control.select__control--menu-is-open');
      
      // Check if click is on select control or any element inside it
      let isClickOnSelect = false;
      if (selectControl) {
        isClickOnSelect = selectControl.contains(clickTarget) || selectControl === clickTarget;
      }
      
      // Only close if click is outside the dropdown menu, menu items, AND the select container
      if (!dropdownMenu && !isClickOnMenu && !isClickOnSelect) {
        if (selectControl) {
          // Find the indicators container
          const indicatorsContainer = selectControl.querySelector('.select__indicators');
          if (indicatorsContainer) {
            // Find the dropdown indicator button - try multiple selectors
            let dropdownIndicator = indicatorsContainer.querySelector('.select__dropdown-indicator');
            
            // If not found by class, try to find div containing SVG (the custom DropdownIndicator)
            if (!dropdownIndicator) {
              const children = Array.from(indicatorsContainer.children);
              dropdownIndicator = children.find(child => {
                // Check if it's a div with SVG inside
                return child.tagName === 'DIV' && child.querySelector('svg');
              });
            }
            
            // If still not found, try finding by aria-hidden attribute
            if (!dropdownIndicator) {
              dropdownIndicator = indicatorsContainer.querySelector('[aria-hidden="true"]');
            }
            
            // Last resort: use the first child div
            if (!dropdownIndicator) {
              dropdownIndicator = indicatorsContainer.querySelector('div');
            }
            
            if (dropdownIndicator) {
              // Trigger mousedown on dropdown indicator to close menu
              const mouseDownEvent = new MouseEvent('mousedown', {
                bubbles: true,
                cancelable: true,
                view: window,
                button: 0,
                clientX: clickTarget.getBoundingClientRect().left,
                clientY: clickTarget.getBoundingClientRect().top,
                detail: 1
              });
              dropdownIndicator.dispatchEvent(mouseDownEvent);
              
              // Also trigger click event for compatibility
              setTimeout(() => {
                const clickEvent = new MouseEvent('click', {
                  bubbles: true,
                  cancelable: true,
                  view: window,
                  button: 0,
                  clientX: clickTarget.getBoundingClientRect().left,
                  clientY: clickTarget.getBoundingClientRect().top
                });
                dropdownIndicator.dispatchEvent(clickEvent);
              }, 10);
            } else {
              // Fallback: Try to blur the input inside
              const input = selectControl.querySelector('input');
              if (input) {
                input.blur();
              }
              
              // Fallback: Dispatch Escape key event
              setTimeout(() => {
                const stillOpen = document.querySelector('.select__control.select__control--menu-is-open');
                if (stillOpen) {
                  const escapeEvent = new KeyboardEvent('keydown', {
                    key: 'Escape',
                    code: 'Escape',
                    keyCode: 27,
                    bubbles: true,
                    cancelable: true
                  });
                  selectControl.dispatchEvent(escapeEvent);
                }
              }, 10);
            }
          }
        }
      }
    }, 0); // Use 0ms delay to let react-select handle the event first
  };

  setupSelectControlClickListener = () => {
    // Prevent duplicate setup
    if (this._selectControlListenerSetup) {
      return;
    }
    
    // Use event delegation on document to handle mousedown on select control
    // Use mousedown instead of click to work better with react-select
    const handleSelectControlMouseDown = (e) => {
      // Check if mousedown is on select__control
      const selectControl = e.target.closest('.select__control');
      if (!selectControl) {
        return;
      }
      
      // Check if click is directly on indicators container or dropdown indicator
      const isClickOnIndicators = e.target.closest('.select__indicators');
      const isClickOnDropdownIndicator = e.target.closest('.select__dropdown-indicator');
      if (isClickOnIndicators || isClickOnDropdownIndicator) {
        // Let react-select handle it normally
        return;
      }
      
      // Check if click is on input field (should let user type)
      const isClickOnInput = e.target.tagName === 'INPUT' || e.target.closest('input');
      if (isClickOnInput) {
        return;
      }
      
      // If clicking on value container or any other part of control (except indicators and input)
      // Trigger click on dropdown indicator immediately
      const indicatorsContainer = selectControl.querySelector('.select__indicators');
      
      if (indicatorsContainer) {
        // Find the dropdown indicator button - try multiple selectors
        let dropdownIndicator = indicatorsContainer.querySelector('.select__dropdown-indicator');
        
        // If not found by class, try to find div containing SVG (the custom DropdownIndicator)
        if (!dropdownIndicator) {
          const children = Array.from(indicatorsContainer.children);
          dropdownIndicator = children.find(child => {
            // Check if it's a div with SVG inside
            return child.tagName === 'DIV' && child.querySelector('svg');
          });
        }
        
        // If still not found, try finding by aria-hidden attribute
        if (!dropdownIndicator) {
          dropdownIndicator = indicatorsContainer.querySelector('[aria-hidden="true"]');
        }
        
        // Last resort: use the first child div
        if (!dropdownIndicator) {
          dropdownIndicator = indicatorsContainer.querySelector('div');
        }
        
        if (dropdownIndicator) {
          // Stop the current event from propagating to prevent react-select from handling it
          e.stopPropagation();
          e.preventDefault();
          
          // Trigger mousedown on dropdown indicator immediately
          const mouseDownEvent = new MouseEvent('mousedown', {
            bubbles: true,
            cancelable: true,
            view: window,
            button: 0,
            clientX: e.clientX,
            clientY: e.clientY,
            detail: 1
          });
          dropdownIndicator.dispatchEvent(mouseDownEvent);
          
          // Also trigger click event for compatibility
          setTimeout(() => {
            const clickEvent = new MouseEvent('click', {
              bubbles: true,
              cancelable: true,
              view: window,
              button: 0,
              clientX: e.clientX,
              clientY: e.clientY
            });
            dropdownIndicator.dispatchEvent(clickEvent);
          }, 10);
        }
      }
    };
    
    // Store reference for cleanup
    this._selectControlClickHandler = handleSelectControlMouseDown;
    
    // Add event listener with capture phase to catch before react-select
    document.addEventListener('mousedown', this._selectControlClickHandler, true);
    this._selectControlListenerSetup = true;
  };

  componentWillUnmount() {
    this._isMounted = false;
    // Remove click outside listener
    document.removeEventListener('click', this.handleClickOutside);
    
    // Remove select control mousedown listener
    if (this._selectControlClickHandler) {
      document.removeEventListener('mousedown', this._selectControlClickHandler, true);
      this._selectControlListenerSetup = false;
    }
  }

  connectMetamask = async () => {
    let account = null;
    if (typeof window.ethereum !== 'undefined') {
      try {
        const accounts = await window.ethereum.request({ method: 'eth_requestAccounts' });
        this.setState({ userWalletID: accounts[0], metamaskStatus: !!accounts[0] });
        account = accounts[0];
        // Listen for account changes
        window.ethereum.on('accountsChanged', (accounts) => {
          // Use arrow function to maintain 'this' context
          this.setState({ userWalletID: accounts[0] });
        });
      } catch (error) {
        console.error('Error connecting to MetaMask:', error);
      }
    } else {
      alert('Please install MetaMask to use this feature');
    }
    return account;
  };

  claimPaymentAction = async (chainId, microjobPaymentAddress, account) => {
    const MicrojobPaymentABI = [
      {
        inputs: [
          { internalType: 'address', name: '_xdaoAddress', type: 'address' },
          { internalType: 'address', name: '_KATAddress', type: 'address' },
          { internalType: 'address', name: '_USDTAddress', type: 'address' },
        ],
        stateMutability: 'nonpayable',
        type: 'constructor',
      },
      {
        anonymous: false,
        inputs: [
          { indexed: true, internalType: 'address', name: 'previousOwner', type: 'address' },
          { indexed: true, internalType: 'address', name: 'newOwner', type: 'address' },
        ],
        name: 'OwnershipTransferred',
        type: 'event',
      },
      {
        anonymous: false,
        inputs: [
          { indexed: true, internalType: 'bytes32', name: 'role', type: 'bytes32' },
          { indexed: true, internalType: 'bytes32', name: 'previousAdminRole', type: 'bytes32' },
          { indexed: true, internalType: 'bytes32', name: 'newAdminRole', type: 'bytes32' },
        ],
        name: 'RoleAdminChanged',
        type: 'event',
      },
      {
        anonymous: false,
        inputs: [
          { indexed: true, internalType: 'bytes32', name: 'role', type: 'bytes32' },
          { indexed: true, internalType: 'address', name: 'account', type: 'address' },
          { indexed: true, internalType: 'address', name: 'sender', type: 'address' },
        ],
        name: 'RoleGranted',
        type: 'event',
      },
      {
        anonymous: false,
        inputs: [
          { indexed: true, internalType: 'bytes32', name: 'role', type: 'bytes32' },
          { indexed: true, internalType: 'address', name: 'account', type: 'address' },
          { indexed: true, internalType: 'address', name: 'sender', type: 'address' },
        ],
        name: 'RoleRevoked',
        type: 'event',
      },
      {
        inputs: [],
        name: 'ADMIN_ROLE',
        outputs: [{ internalType: 'bytes32', name: '', type: 'bytes32' }],
        stateMutability: 'view',
        type: 'function',
      },
      {
        inputs: [],
        name: 'DEFAULT_ADMIN_ROLE',
        outputs: [{ internalType: 'bytes32', name: '', type: 'bytes32' }],
        stateMutability: 'view',
        type: 'function',
      },
      {
        inputs: [],
        name: 'KATAddress',
        outputs: [{ internalType: 'address', name: '', type: 'address' }],
        stateMutability: 'view',
        type: 'function',
      },
      {
        inputs: [],
        name: 'TRANSACTION_IMPLEMENTOR_ROLE',
        outputs: [{ internalType: 'bytes32', name: '', type: 'bytes32' }],
        stateMutability: 'view',
        type: 'function',
      },
      {
        inputs: [],
        name: 'USDTAddress',
        outputs: [{ internalType: 'address', name: '', type: 'address' }],
        stateMutability: 'view',
        type: 'function',
      },
      {
        inputs: [
          { internalType: 'uint256', name: '_USDTAmount', type: 'uint256' },
          { internalType: 'uint256', name: '_EquivalentKATPerUSDT', type: 'uint256' },
        ],
        name: 'calculateBalances',
        outputs: [
          {
            components: [
              { internalType: 'uint256', name: 'katBalanceOnUSDTWei', type: 'uint256' },
              { internalType: 'uint256', name: 'usdtWeiBalance', type: 'uint256' },
            ],
            internalType: 'struct MicroJobPayment.Balances',
            name: 'retBalances',
            type: 'tuple',
          },
        ],
        stateMutability: 'view',
        type: 'function',
      },
      { inputs: [], name: 'claim', outputs: [], stateMutability: 'nonpayable', type: 'function' },
      {
        inputs: [{ internalType: 'address', name: '', type: 'address' }],
        name: 'claimerList',
        outputs: [
          { internalType: 'address', name: 'wallet', type: 'address' },
          { internalType: 'uint256', name: 'withdrawableinKAT', type: 'uint256' },
          { internalType: 'uint256', name: 'withdrawableinUSDT', type: 'uint256' },
        ],
        stateMutability: 'view',
        type: 'function',
      },
      {
        inputs: [{ internalType: 'bytes32', name: 'role', type: 'bytes32' }],
        name: 'getRoleAdmin',
        outputs: [{ internalType: 'bytes32', name: '', type: 'bytes32' }],
        stateMutability: 'view',
        type: 'function',
      },
      {
        inputs: [
          { internalType: 'bytes32', name: 'role', type: 'bytes32' },
          { internalType: 'address', name: 'account', type: 'address' },
        ],
        name: 'grantRole',
        outputs: [],
        stateMutability: 'nonpayable',
        type: 'function',
      },
      {
        inputs: [
          { internalType: 'bytes32', name: 'role', type: 'bytes32' },
          { internalType: 'address', name: 'account', type: 'address' },
        ],
        name: 'hasRole',
        outputs: [{ internalType: 'bool', name: '', type: 'bool' }],
        stateMutability: 'view',
        type: 'function',
      },
      {
        inputs: [{ internalType: 'uint256', name: '', type: 'uint256' }],
        name: 'lastLpHolderList',
        outputs: [
          { internalType: 'address', name: 'wallet', type: 'address' },
          { internalType: 'uint256', name: 'balance', type: 'uint256' },
        ],
        stateMutability: 'view',
        type: 'function',
      },
      {
        inputs: [],
        name: 'owner',
        outputs: [{ internalType: 'address', name: '', type: 'address' }],
        stateMutability: 'view',
        type: 'function',
      },
      {
        inputs: [],
        name: 'paymentPerSession',
        outputs: [{ internalType: 'uint256', name: '', type: 'uint256' }],
        stateMutability: 'view',
        type: 'function',
      },
      {
        inputs: [{ internalType: 'uint256', name: '', type: 'uint256' }],
        name: 'purchaseTimestamps',
        outputs: [{ internalType: 'uint256', name: '', type: 'uint256' }],
        stateMutability: 'view',
        type: 'function',
      },
      { inputs: [], name: 'renounceOwnership', outputs: [], stateMutability: 'nonpayable', type: 'function' },
      {
        inputs: [
          { internalType: 'bytes32', name: 'role', type: 'bytes32' },
          { internalType: 'address', name: 'account', type: 'address' },
        ],
        name: 'renounceRole',
        outputs: [],
        stateMutability: 'nonpayable',
        type: 'function',
      },
      {
        inputs: [
          { internalType: 'bytes32', name: 'role', type: 'bytes32' },
          { internalType: 'address', name: 'account', type: 'address' },
        ],
        name: 'revokeRole',
        outputs: [],
        stateMutability: 'nonpayable',
        type: 'function',
      },
      {
        inputs: [{ internalType: 'uint256', name: '_paymentPerSession', type: 'uint256' }],
        name: 'setPaymentPerSession',
        outputs: [],
        stateMutability: 'nonpayable',
        type: 'function',
      },
      {
        inputs: [
          { internalType: 'address[]', name: 'wallets', type: 'address[]' },
          { internalType: 'uint256[]', name: 'numbersOfCompletedSession', type: 'uint256[]' },
          { internalType: 'uint256', name: 'usdtAmount', type: 'uint256' },
          { internalType: 'uint256', name: 'equivalentKATPerUSDT', type: 'uint256' },
        ],
        name: 'submitCalculationData',
        outputs: [],
        stateMutability: 'nonpayable',
        type: 'function',
      },
      {
        inputs: [{ internalType: 'bytes4', name: 'interfaceId', type: 'bytes4' }],
        name: 'supportsInterface',
        outputs: [{ internalType: 'bool', name: '', type: 'bool' }],
        stateMutability: 'view',
        type: 'function',
      },
      {
        inputs: [{ internalType: 'uint256', name: '', type: 'uint256' }],
        name: 'transactionDone',
        outputs: [{ internalType: 'bool', name: '', type: 'bool' }],
        stateMutability: 'view',
        type: 'function',
      },
      {
        inputs: [{ internalType: 'address', name: 'newOwner', type: 'address' }],
        name: 'transferOwnership',
        outputs: [],
        stateMutability: 'nonpayable',
        type: 'function',
      },
      {
        inputs: [],
        name: 'viewAllCalculations',
        outputs: [
          {
            components: [
              {
                components: [
                  { internalType: 'address', name: 'wallet', type: 'address' },
                  { internalType: 'uint256', name: 'withdrawableinKAT', type: 'uint256' },
                  { internalType: 'uint256', name: 'withdrawableinUSDT', type: 'uint256' },
                ],
                internalType: 'struct MicroJobPayment.Claimer[]',
                name: 'claimers',
                type: 'tuple[]',
              },
              { internalType: 'uint256', name: 'timestamp', type: 'uint256' },
            ],
            internalType: 'struct MicroJobPayment.PaymentCalculation[]',
            name: '',
            type: 'tuple[]',
          },
        ],
        stateMutability: 'view',
        type: 'function',
      },
      {
        inputs: [],
        name: 'viewAllClaimTransactions',
        outputs: [
          {
            components: [
              { internalType: 'address', name: 'claimer', type: 'address' },
              { internalType: 'uint256', name: 'claimedAmountInKAT', type: 'uint256' },
              { internalType: 'uint256', name: 'claimedAmountInUSDT', type: 'uint256' },
              { internalType: 'uint256', name: 'timestamp', type: 'uint256' },
            ],
            internalType: 'struct MicroJobPayment.ClaimTransaction[]',
            name: '',
            type: 'tuple[]',
          },
        ],
        stateMutability: 'view',
        type: 'function',
      },
      {
        inputs: [],
        name: 'xdaoAddress',
        outputs: [{ internalType: 'contract IDao', name: '', type: 'address' }],
        stateMutability: 'view',
        type: 'function',
      },
    ];
    let web3 = null;
    web3 = new Web3(window.ethereum);

    if (microjobPaymentAddress) {

      const MicroJobPaymentContract = new web3.eth.Contract(MicrojobPaymentABI, microjobPaymentAddress);

      try {
        const gasPrice = web3.utils.toWei('80', 'gwei'); // Đặt giá gas là 80 Gwei

        let result = await MicroJobPaymentContract.methods.claim().send({
          from: account,
          gas: 3000000, // Đặt giới hạn gas cao hơn nếu cần
          gasPrice: gasPrice, // Sử dụng giá gas đã chuyển đổi
        });
      } catch (error) {
        // Xử lý lỗi nếu cần thiết

        return [];
      }
      // Sau khi lấy xong thì có thể thực hiện các bước khác tại đây
    }
  };
  claimPayment = async (chainId, microjobPaymentAddress) => {
    const account = await this.connectMetamask();
    let isValid = true;
    let web3 = null;
    if (account && typeof window.ethereum !== 'undefined') {
      if (window.ethereum) {
        web3 = new Web3(window.ethereum);
      } else {
        console.error('Ethereum object not found, make sure you have a web3 provider.');
        return false;
      }
      const networkId = await web3.eth.net.getId();
      if (parseFloat(networkId) === parseFloat(chainId)) {
        await this.claimPaymentAction(chainId, microjobPaymentAddress, this.state.userWalletID);
      } else {
        alert('Wrong network. Please change your wallet network to Chain ID ' + chainId);
        window.ethereum.request({
          method: 'wallet_switchEthereumChain',
          params: [{ chainId: web3.utils.toHex(chainId) }],
        });
        isValid = false;
      }
    }
    return isValid;
  };

  validateFormData = async ({ phoneNumber, endYear, publicUsername, firstName, email, dateOfBirth, ...values }) => {
    const { user, checkPublicUsername } = this.props;
    const errors = {};
    
    // Validate firstName (required)
    if (!firstName || !firstName.trim().length) {
      errors.firstName = I18n.t('text_field_required_error');
    }

    // Validate email (required)
    if (!email || !email.trim().length) {
      errors.email = I18n.t('text_field_required_error');
    } else {
      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email.trim())) {
        errors.email = I18n.t('invalid_email_format') || 'Email không hợp lệ';
      }
    }

    // Validate dateOfBirth (required when updating profile after login)
    // This form is only used for profile editing after login, so dateOfBirth is always required here
    if (!dateOfBirth || !dateOfBirth.trim().length) {
      errors.dateOfBirth = I18n.t('text_field_required_error');
    } else {
      // Validate date format
      const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
      if (!dateRegex.test(dateOfBirth.trim())) {
        // If format is not YYYY-MM-DD, try to parse it
        const parsedDate = moment(dateOfBirth, ['YYYY-MM-DD', 'DD/MM/YYYY', 'MM/DD/YYYY', 'YYYY/MM/DD'], true);
        if (!parsedDate.isValid()) {
          errors.dateOfBirth = I18n.t('invalid_date_format') || 'Ngày sinh không hợp lệ';
        }
      }
    }

    // Get user type to determine which fields are required
    const userType = user?.mvpUserType || user?.type || '';
    const isExplorer = userType === 'explorer' || userType === 'both' || userType === 'senior';
    const isGuide = userType === 'guide' || userType === 'both' || userType === 'buddy';

    // Validate favouriteTopics (required for explorer or both)
    if (isExplorer) {
      if (!values.favouriteTopics || !Array.isArray(values.favouriteTopics) || values.favouriteTopics.length === 0) {
        errors.favouriteTopics = I18n.t('text_field_required_error');
      } else {
        // Check if only default option is selected (value is empty string)
        const validTopics = values.favouriteTopics.filter(topic => topic && topic.value && topic.value !== '');
        if (validTopics.length === 0) {
          errors.favouriteTopics = I18n.t('text_field_required_error');
        }
      }
    }

    // Validate sharingTopics (required for guide or both)
    if (isGuide) {
      if (!values.sharingTopics || !Array.isArray(values.sharingTopics) || values.sharingTopics.length === 0) {
        errors.sharingTopics = I18n.t('text_field_required_error');
      } else {
        // Check if only default option is selected (value is empty string)
        const validTopics = values.sharingTopics.filter(topic => topic && topic.value && topic.value !== '');
        if (validTopics.length === 0) {
          errors.sharingTopics = I18n.t('text_field_required_error');
        }
      }
    }

    // SELECT_FIELDS.forEach((key) => {
    //   if (!values[key] || !values[key].value) {
    //     errors[key] = 'required';
    //   }
    // });

    // MULTI_SELECT_FIELDS.forEach((key) => {
    //   if (!values[key] || !values[key].length) {
    //     errors[key] = 'required';
    //   }
    // });

    // if (!phoneNumber || !phoneNumber.number || !utils.validatePhoneNumber(phoneNumber.dialCode, phoneNumber.number)) {
    //   errors.phoneNumber = 'incorrect phone number';
    // }

    // if (!publicUsername.trim().length) {
    //   errors.publicUsername = 'required';
    // } else if (publicUsername.trim().length < 5) {
    //   errors.publicUsername = 'must be at least 5 characters';
    // } else if (publicUsername.trim().length > 50) {
    //   errors.publicUsername = 'max length exceeded';
    // } else if (/[^a-zA-Z0-9.]/g.test(publicUsername.trim())) {
    //   errors.publicUsername = 'contains invalid characters';
    // } else if (publicUsername !== user.publicUsername) {
    //   const result = await checkPublicUsername(publicUsername);
    //   if (result.message) {
    //     errors.publicUsername = result.message;
    //   }
    // }
    return errors;
  };

  onBtnUpdateClick = async (values) => {
    const { updateUserInfo, submitErrors, fetchUserMvp, dispatch } = this.props;
    const errors = Object.keys(submitErrors).length ? submitErrors : await this.validateFormData(values);
    if (Object.keys(errors).length) throw new SubmissionError(errors);
    const selectFieldValues = SELECT_FIELDS.reduce((result, key) => {
      result[key] = values[key]?.value;
      return result;
    }, {});
    const multiSelectFieldValues = MULTI_SELECT_FIELDS.reduce((result, key) => {
      if (values[key] && Array.isArray(values[key])) {
        if (key === 'sharingTopics') {
          // For sharingTopics, extract both level1Id and level2Id
          // value is now a string like "level1Id_level2Id", or we have level1Id/level2Id directly on the option
          result[key] = values[key].reduce((ids, item) => {
            // Check if we have level1Id and level2Id directly on the option (from getSharingCategoryOptions)
            if (item.level1Id && item.level2Id) {
              ids.push(item.level1Id);
              ids.push(item.level2Id);
            } else {
              // Fallback: try to parse string value format "level1Id_level2Id"
              const value = item.value || item;
              if (typeof value === 'string' && value.includes('_')) {
                const [level1IdStr, level2IdStr] = value.split('_');
                if (level1IdStr && level2IdStr) {
                  ids.push(level1IdStr);
                  ids.push(level2IdStr);
                }
              } else if (value) {
                // Last fallback: backward compatibility
                ids.push(value);
              }
            }
            return ids;
          }, []);
        } else {
          result[key] = values[key].map((item) => item.value || item);
        }
      }
      return result;
    }, {});
    const finalValues = { ...values, ...selectFieldValues, ...multiSelectFieldValues };
    
    
    // Dispatch updateUserInfo action
    dispatch(updateUserInfo({ ...values, ...selectFieldValues, ...multiSelectFieldValues }, (response) => {
      // Kiểm tra response để hiển thị toast phù hợp
      if (response && response.status === 'OK') {
        
        // Fetch user data để cập nhật Redux store và localStorage
        const { fetchUserMvp } = this.props;
        fetchUserMvp((updatedUser) => {
          // Force reinitialize form với dữ liệu mới
          setTimeout(() => {
            // Xử lý phoneNumber để đảm bảo là string
            let phoneNumberValue = '';
            if (updatedUser.phoneNumber) {
              // Nếu phoneNumber là object (có thể có dialCode và number)
              if (typeof updatedUser.phoneNumber === 'object' && updatedUser.phoneNumber !== null) {
                if (updatedUser.phoneNumber.number) {
                  // Nếu có dialCode, kết hợp dialCode + number
                  phoneNumberValue = updatedUser.phoneNumber.dialCode ? `${updatedUser.phoneNumber.dialCode}${updatedUser.phoneNumber.number}` : updatedUser.phoneNumber.number;
                } else {
                  // Nếu là object nhưng không có number, chuyển thành string rỗng
                  phoneNumberValue = '';
                }
              }
              // Nếu phoneNumber là string, giữ nguyên
              else if (typeof updatedUser.phoneNumber === 'string') {
                phoneNumberValue = updatedUser.phoneNumber;
              }
              // Các trường hợp khác, chuyển thành string
              else {
                phoneNumberValue = String(updatedUser.phoneNumber);
              }
            }
            
            // Sử dụng change action để update từng field
            this.props.dispatch(change(FORM_NAME, 'firstName', updatedUser.firstName || updatedUser.name || ''));
            this.props.dispatch(change(FORM_NAME, 'dateOfBirth', updatedUser.dateOfBirth || ''));
            this.props.dispatch(change(FORM_NAME, 'phoneNumber', phoneNumberValue));
            this.props.dispatch(change(FORM_NAME, 'email', updatedUser.email || ''));
            
            // Update favouriteTopics if available
            if (updatedUser.favouriteTopics && Array.isArray(updatedUser.favouriteTopics) && this.state.categories.length > 0) {
              const favouriteTopicsOptions = updatedUser.favouriteTopics.map(topicId => {
                const topicIdStr = topicId?.toString() || topicId;
                const category = this.findCategoryById(topicIdStr);
                if (category) {
                  return {
                    value: category._id,
                    label: category.name
                  };
                }
                return null;
              }).filter(Boolean);
              if (favouriteTopicsOptions.length > 0) {
                this.props.dispatch(change(FORM_NAME, 'favouriteTopics', favouriteTopicsOptions));
              }
            } else if (updatedUser.favouriteTopics && Array.isArray(updatedUser.favouriteTopics) && this.state.categories.length === 0) {
              // If categories not loaded yet, fetch them first
              this.fetchCategories().then(() => {
                if (this._isMounted) {
                  setTimeout(() => {
                    this.updateFavouriteTopicsInForm();
                  }, 100);
                }
              });
            }
            
            // Update sharingTopics if available
            if (updatedUser.sharingTopics && Array.isArray(updatedUser.sharingTopics)) {
              if (this.state.categories.length > 0) {
                // Categories are loaded, map IDs to options
                // Get all available sharing category options
                const allSharingOptions = this.getSharingCategoryOptions();
                
                // sharingTopics contains pairs of IDs: [level1Id1, level2Id1, level1Id2, level2Id2, ...]
                const sharingTopicsOptions = [];
                
                // Process IDs in pairs (every two IDs form a pair)
                for (let i = 0; i < updatedUser.sharingTopics.length; i += 2) {
                  if (i + 1 < updatedUser.sharingTopics.length) {
                    const level1Id = updatedUser.sharingTopics[i];
                    const level2Id = updatedUser.sharingTopics[i + 1];
                    
                    // Convert to strings for comparison
                    const level1IdStr = level1Id?.toString() || level1Id;
                    const level2IdStr = level2Id?.toString() || level2Id;
                    
                    // Find matching option from allSharingOptions by comparing level1Id and level2Id
                    const matchingOption = allSharingOptions.find(option => {
                      const optLevel1IdStr = option.level1Id?.toString() || option.level1Id;
                      const optLevel2IdStr = option.level2Id?.toString() || option.level2Id;
                      return optLevel1IdStr === level1IdStr && optLevel2IdStr === level2IdStr;
                    });
                    
                    if (matchingOption) {
                      sharingTopicsOptions.push(matchingOption);
                    }
                  }
                }
                
                // Always update form, even if empty array (to clear previous values if needed)
                this.props.dispatch(change(FORM_NAME, 'sharingTopics', sharingTopicsOptions));
              } else {
                // If categories not loaded yet, fetch them first then update
                const { getCategorySessions } = this.props;
                getCategorySessions().then((categories) => {
                  if (categories && Array.isArray(categories) && this._isMounted) {
                    // Update state with categories
                    this.setState({ categories }, () => {
                      // Now that categories are loaded, map sharingTopics
                      if (updatedUser.sharingTopics && Array.isArray(updatedUser.sharingTopics)) {
                        // Get all available sharing category options
                        const allSharingOptions = this.getSharingCategoryOptions();
                        
                        // sharingTopics contains pairs of IDs: [level1Id1, level2Id1, level1Id2, level2Id2, ...]
                        const sharingTopicsOptions = [];
                        
                        // Process IDs in pairs (every two IDs form a pair)
                        for (let i = 0; i < updatedUser.sharingTopics.length; i += 2) {
                          if (i + 1 < updatedUser.sharingTopics.length) {
                            const level1Id = updatedUser.sharingTopics[i];
                            const level2Id = updatedUser.sharingTopics[i + 1];
                            
                            // Convert to strings for comparison
                            const level1IdStr = level1Id?.toString() || level1Id;
                            const level2IdStr = level2Id?.toString() || level2Id;
                            
                            // Find matching option from allSharingOptions by comparing level1Id and level2Id
                            const matchingOption = allSharingOptions.find(option => {
                              const optLevel1IdStr = option.level1Id?.toString() || option.level1Id;
                              const optLevel2IdStr = option.level2Id?.toString() || option.level2Id;
                              return optLevel1IdStr === level1IdStr && optLevel2IdStr === level2IdStr;
                            });
                            
                            if (matchingOption) {
                              sharingTopicsOptions.push(matchingOption);
                            }
                          }
                        }
                        // Always update form, even if empty array
                        this.props.dispatch(change(FORM_NAME, 'sharingTopics', sharingTopicsOptions));
                      } else {
                        // Ensure it's always an array
                        this.props.dispatch(change(FORM_NAME, 'sharingTopics', []));
                      }
                    });
                  }
                }).catch((error) => {
                  console.error('Error fetching categories:', error);
                  // On error, ensure sharingTopics is at least an empty array
                  this.props.dispatch(change(FORM_NAME, 'sharingTopics', []));
                });
              }
            } else {
              // If no sharingTopics, ensure it's an empty array
              this.props.dispatch(change(FORM_NAME, 'sharingTopics', []));
            }
          }, 100);
          
          // Hiển thị thông báo thành công
          toast.success('Thông tin đã được cập nhật thành công!', {
            position: 'top-center',
            autoClose: 3000,
            hideProgressBar: true,
            closeOnClick: true,
            pauseOnHover: false,
            draggable: false,
            progress: undefined,
            theme: 'colored',
            className: 'toast-success-container toast-success-container-after',
          });
        });
      } else {
        // Hiển thị thông báo lỗi
        toast.error('Có lỗi xảy ra khi cập nhật thông tin. Vui lòng thử lại!', {
          position: 'top-center',
          autoClose: 3000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: false,
          draggable: false,
          progress: undefined,
          theme: 'colored',
          className: 'toast-error-container toast-error-container-after',
        });
      }
    }));
  };

  handleDeleteAccountClick = () => {
    this.setState({ showDeleteModal: true });
  };

  closeDeleteModal = () => {
    this.setState({ showDeleteModal: false });
  };

  confirmDeleteAccount = async () => {
    // Prevent multiple calls - check both ref (synchronous) and state
    // Using ref prevents race condition since setState is async
    if (this._isDeletingAccountRef || this.state.isDeletingAccount) {
      return;
    }

    // Set both ref (immediate, synchronous) and state (async) to prevent duplicate calls
    this._isDeletingAccountRef = true;
    this.setState({ isDeletingAccount: true });
    this.closeDeleteModal();

    try {
      const { logOutMvp, user } = this.props;
      const userData = localStorage.getItem('ced:user-info');
      const userStorage = userData ? JSON.parse(userData) : null;
      
      if (!userStorage || !userStorage.accessToken) {
        this._isDeletingAccountRef = false;
        if (this._isMounted) {
          this.setState({ isDeletingAccount: false });
        }
        toast.error(I18n.t('something_wrong'), {
          position: 'top-center',
          autoClose: 3000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: false,
          draggable: false,
          progress: undefined,
          theme: 'colored',
        });
        return;
      }
      
      // Delete account using MVP endpoint
      const response = await client.sendRequest({
        path: '/mvp/remove-user',
        method: 'POST',
        params: {},
        headers: {
          'authorization': userStorage.accessToken,
        },
      });

      if (response && response.status === 'OK') {
        // Call logout API first
        const accessToken = user?.accessToken || userStorage.accessToken;
        if (accessToken) {
          try {
            await logOutMvp(accessToken);
          } catch (logoutError) {
            // Continue with cleanup even if logout fails
          }
        }

        // Clear storage immediately after logout
        localStorage.removeItem('ced:pathBeforeSignIn');
        localStorage.removeItem('ced:user-info');
        sessionStorage.removeItem('ced:previousUrl');
        
        // Remove cookies
        Utils.removeCookie('_RFT');

        // Save toast message to sessionStorage to show after redirect
        // (cannot show toast here because component will unmount on redirect)
        sessionStorage.setItem('ced:toast-message', I18n.t('delete_account_successful'));
        sessionStorage.setItem('ced:toast-type', 'success');

        // Redirect to home page immediately
        // Toast will be shown by App.js when it loads
        window.location.replace('/');
      } else {
        this._isDeletingAccountRef = false;
        if (this._isMounted) {
          this.setState({ isDeletingAccount: false });
        }
        throw new Error(response?.message || 'Failed to delete account');
      }
    } catch (error) {
      this._isDeletingAccountRef = false;
      if (this._isMounted) {
        this.setState({ isDeletingAccount: false });
      }
      
      // Only show error toast if component is still mounted
      if (this._isMounted) {
        toast.error(error?.message || I18n.t('something_wrong'), {
          position: 'top-center',
          autoClose: 3000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: false,
          draggable: false,
          progress: undefined,
          theme: 'colored',
          className: 'toast-error-container toast-error-container-after',
        });
      }
    }
  };

  renderInput = ({ input, label, type, meta: { touched, error }, disabled, col = 6, required = false }) => {
    return (
      <div className={`form-item col-12 col-xl-${col} col-lg-${col} col-md-12 col-sm-12`}>
        <span className="label d-block mt-3 mb-2" style={{ color: '#FFFFFF' }}>
          {label} {required && <span className="text-danger">*</span>} {touched && error && <small className="error" style={{ color: '#FF445D', marginLeft: '8px' }}>{error}</small>}
        </span>
        <input 
          {...input} 
          disabled={disabled} 
          className="w-100 form-control" 
          type={type}
          style={{
            color: '#FFFFFF',
            backgroundColor: '#1F2937',
            border: touched && error ? '1px solid #FF445D' : '1px solid #374151',
            borderRadius: '8px',
            padding: '12px 16px',
            fontSize: '16px'
          }}
        />
      </div>
    );
  };

  renderMultiSelect = ({ input, label, meta: { touched, error }, disabled, col = 6, required = false, options = [] }) => {
    const language = localStorage.getItem('ced:language') || 'vi';
    // Chỉ dùng options thực sự, không thêm defaultOption vào dropdown
    const selectOptions = options;
    
    const currentValue = Array.isArray(input.value) ? input.value : [];

    return (
      <div className={`form-item col-12 col-xl-${col} col-lg-${col} col-md-12 col-sm-12`}>
        <span className="label d-block mt-3 mb-2" style={{ color: '#FFFFFF' }}>
          {label} {required && <span className="text-danger">*</span>} {touched && error && <small className="error" style={{ color: '#FF445D', marginLeft: '8px' }}>{error}</small>}
        </span>
        <Select
          isMulti
          isDisabled={disabled}
          isClearable={false}
          closeMenuOnSelect={false}
          hideSelectedOptions={false}
          options={selectOptions}
          value={currentValue}
          getOptionValue={(option) => option.value}
          getOptionLabel={(option) => option.label}
          onChange={(selectedOptions) => {
            // Đảm bảo luôn là array, kể cả khi selectedOptions là null hoặc undefined
            const newValue = Array.isArray(selectedOptions) ? selectedOptions : [];
            input.onChange(newValue);
          }}
          onBlur={() => input.onBlur()}
          onFocus={() => input.onFocus && input.onFocus()}
          className="w-100"
          classNamePrefix="select"
          placeholder={I18n.t('help_suggest_suitable_topic') || 'Help suggest suitable topic'}
          menuPosition="absolute"
          styles={{
            control: (base, state) => ({
              ...base,
              backgroundColor: '#1F2937',
              borderColor: touched && error ? '#FF445D' : '#374151',
              borderRadius: '8px',
              minHeight: '46px',
              color: '#FFFFFF',
              cursor: 'pointer',
              '&:hover': {
                borderColor: touched && error ? '#FF445D' : '#374151',
              },
            }),
            menu: (base) => ({
              ...base,
              backgroundColor: '#3E4754',
              borderRadius: '12px',
              zIndex: 9999,
              fontSize: '18px',
              padding: '10px 20px',
              position: 'absolute',
              pointerEvents: 'auto',
            }),
            menuPortal: (base) => ({
              ...base,
              zIndex: 9999,
            }),
            option: (base, state) => ({
              ...base,
              backgroundColor: state.isFocused ? '#374151' : '#3E4754',
              color: state.isSelected ? '#E87623' : '#FFFFFF',
              fontSize: '18px',
              padding: '10px',
              borderRadius: '8px',
              borderBottom: '1px solid #374151',
              '&:last-child': {
                borderBottom: 'none',
              },
              '&:hover': {
                backgroundColor: '#374151',
              },
              '&:active': {
                backgroundColor: '#4B5563',
              },
            }),
            multiValue: () => ({
              display: 'none', // Ẩn multiValue tags
            }),
            multiValueLabel: () => ({
              display: 'none',
            }),
            multiValueRemove: () => ({
              display: 'none', // Ẩn dấu X
            }),
            placeholder: (base) => ({
              ...base,
              color: '#9CA3AF',
            }),
            singleValue: (base) => ({
              ...base,
              color: '#FFFFFF',
            }),
            input: (base) => ({
              ...base,
              color: '#FFFFFF',
            }),
            indicatorsContainer: (base) => ({
              ...base,
              '& .select__indicator': {
                color: '#9CA3AF',
                '&:hover': {
                  color: '#9CA3AF',
                },
              },
              '& .select__clear-indicator': {
                display: 'none', // Ẩn dấu X để xóa toàn bộ
              },
            }),
            dropdownIndicator: (base) => ({
              ...base,
              color: '#9CA3AF',
              '&:hover': {
                color: '#9CA3AF',
              },
              '& svg': {
                fill: '#9CA3AF',
              },
            }),
          }}
          components={{
            IndicatorSeparator: () => null, // Bỏ dấu gạch dọc
            MultiValueContainer: () => null, // Ẩn container của multiValue
            MultiValueRemove: () => null, // Ẩn dấu X
            ClearIndicator: () => null, // Ẩn dấu X để xóa toàn bộ
            Option: (props) => {
              const { isSelected, label, innerRef, innerProps, data, isFocused, selectProps } = props;
              const options = props.selectProps?.options || [];
              const isLast = options[options.length - 1] === data;
              
              // Get the onChange handler from selectProps
              const onChange = selectProps?.onChange;
              const currentValue = Array.isArray(selectProps?.value) ? selectProps.value : [];
              
              // Wrap all event handlers to log them and manually trigger onChange if needed
              const originalOnClick = innerProps?.onClick;
              const originalOnMouseDown = innerProps?.onMouseDown;
              
              const handleOptionSelect = (e) => {
                if (onChange) {
                  // If already selected, remove it; otherwise add it
                  let newValue;
                  if (isSelected) {
                    newValue = currentValue.filter(v => v.value !== data.value);
                  } else {
                    newValue = [...currentValue, data];
                  }
                  
                  onChange(newValue);
                }
              };
              
              const wrappedOnClick = (e) => {
                handleOptionSelect(e);
                if (originalOnClick) {
                  originalOnClick(e);
                }
              };
              
              const wrappedOnMouseDown = (e) => {
                handleOptionSelect(e);
                if (originalOnMouseDown) {
                  originalOnMouseDown(e);
                }
              };
              
              // Merge all props but override event handlers with our wrapped versions
              const mergedProps = {
                ...innerProps,
                onClick: wrappedOnClick,
                onMouseDown: wrappedOnMouseDown,
              };
              
              return (
                <div
                  ref={innerRef}
                  {...mergedProps}
                  style={{
                    padding: '10px',
                    borderRadius: '8px',
                    backgroundColor: isFocused ? '#374151' : '#3E4754',
                    color: isSelected ? '#E87623' : '#FFFFFF',
                    fontSize: '18px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    cursor: 'pointer',
                    borderBottom: isLast ? 'none' : '1px solid #374151',
                    pointerEvents: 'auto',
                    userSelect: 'none',
                  }}
                >
                  <span>{label}</span>
                  {isSelected && (
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginLeft: '8px', flexShrink: 0 }}>
                      <path d="M13.3333 4L6 11.3333L2.66667 8" stroke="#E87623" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  )}
                </div>
              );
            },
            ValueContainer: ({ children, ...props }) => {
              const selectedValues = props.getValue() || [];
              const selectedLabels = selectedValues.map(option => option?.label || option).join(', ');
              
              // Handle click on value container to open menu
              const handleContainerMouseDown = (e) => {
                // Find the control element
                const control = e.currentTarget.closest('.select__control');
                if (control) {
                  // Check if menu is not already open
                  const isMenuOpen = control.classList.contains('select__control--menu-is-open');
                  if (!isMenuOpen) {
                    // Find the indicators container (select__indicators)
                    const indicatorsContainer = control.querySelector('.select__indicators');
                    if (indicatorsContainer) {
                      // Trigger click on indicators container
                      indicatorsContainer.click();
                    } else {
                      // Fallback: find dropdown indicator directly
                      const indicator = control.querySelector('.select__dropdown-indicator');
                      if (indicator) {
                        indicator.click();
                      } else {
                        // Fallback: find input and focus it
                        const input = control.querySelector('input');
                        if (input) {
                          input.focus();
                          input.click();
                        }
                      }
                    }
                  }
                }
              };
              
              return (
                <div 
                  className="select__value-container" 
                  onMouseDown={handleContainerMouseDown}
                  style={{ 
                    display: 'flex', 
                    flexWrap: 'nowrap', 
                    flex: 1, 
                    alignItems: 'center',
                    minHeight: '46px',
                    padding: '2px 8px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    cursor: 'pointer'
                  }}
                >
                  {selectedValues.length > 0 ? (
                    <div 
                      onMouseDown={(e) => {
                        // Find the control element
                        const control = e.currentTarget.closest('.select__control');
                        if (control) {
                          // Check if menu is not already open
                          const isMenuOpen = control.classList.contains('select__control--menu-is-open');
                          if (!isMenuOpen) {
                            // Stop propagation to prevent handleClickOutside from interfering
                            e.stopPropagation();
                            // Find the indicators container (select__indicators)
                            const indicatorsContainer = control.querySelector('.select__indicators');
                            if (indicatorsContainer) {
                              // Simply click on indicators container - react-select will handle it
                              indicatorsContainer.click();
                            } else {
                              // Fallback: find dropdown indicator directly
                              const indicator = control.querySelector('.select__dropdown-indicator');
                              if (indicator) {
                                indicator.click();
                              } else {
                                // Fallback: find input and focus it
                                const input = control.querySelector('input');
                                if (input) {
                                  input.focus();
                                  input.click();
                                }
                              }
                            }
                          }
                        }
                      }}
                      style={{ 
                        color: '#FFFFFF', 
                        fontSize: '16px', 
                        lineHeight: '1.5',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        width: '100%',
                        cursor: 'pointer',
                        userSelect: 'none'
                      }}
                    >
                      {selectedLabels}
                    </div>
                  ) : (
                    children
                  )}
                </div>
              );
            },
            DropdownIndicator: (props) => {
              return (
                <div {...props.innerProps} style={{ 
                  padding: '8px 0',
                  margin: '0 16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer'
                }}>
                  <svg width="12" height="12" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M6 8L0 0L12 0L6 8Z" fill="#9CA3AF"/>
                  </svg>
                </div>
              );
            },
          }}
        />
      </div>
    );
  };

  renderDatePicker = ({ input, label, meta: { touched, error }, disabled, col = 6, required = false }) => {
    // Xử lý giá trị để đảm bảo DatePicker hiển thị đúng
    let dateValue = null;
    if (input.value) {
      // Nếu giá trị là string, chuyển đổi thành moment object
      if (typeof input.value === 'string') {
        // Thử parse với nhiều format khác nhau
        dateValue = moment(input.value, ['YYYY-MM-DD', 'DD/MM/YYYY', 'MM/DD/YYYY', 'YYYY/MM/DD'], true);
        
        // Nếu không parse được, thử parse với moment mặc định
        if (!dateValue.isValid()) {
          dateValue = moment(input.value);
        }
        
      } else if (moment.isMoment(input.value)) {
        dateValue = input.value;
      }
    }

    return (
      <div className={`form-item col-12 col-xl-${col} col-lg-${col} col-md-12 col-sm-12`}>
        <span className="label d-block mt-3 mb-2" style={{ color: '#FFFFFF' }}>
          {label} {required && <span className="text-danger">*</span>} {touched && error && <small className="error" style={{ color: '#FF445D', marginLeft: '8px' }}>{error}</small>}
        </span>
        <div className="custom-datepicker-wrapper">
          <DatePicker
            key={`datepicker-${input.value || 'empty'}`}
            disabled={disabled}
            className="w-100"
            style={{
              color: '#FFFFFF',
              backgroundColor: '#1F2937',
              border: touched && error ? '1px solid #FF445D' : '1px solid #374151',
              borderRadius: '8px',
              padding: '12px 16px',
              fontSize: '16px',
              height: '48px',
              cursor: disabled ? 'not-allowed' : 'pointer'
            }}
            format="DD/MM/YYYY"
            placeholder="Chọn ngày sinh"
            value={dateValue && dateValue.isValid() ? dateValue : null}
            onChange={(date) => {
              if (date && date.isValid()) {
                input.onChange(date.format('YYYY-MM-DD'));
              } else {
                input.onChange('');
              }
            }}
            onBlur={input.onBlur}
          />
          <style dangerouslySetInnerHTML={{
            __html: `
              .custom-datepicker-wrapper .ant-picker-input input {
                border: none !important;
                padding-left: 0 !important;
                background: transparent !important;
                box-shadow: none !important;
                color: #ffffff !important;
              }
              .custom-datepicker-wrapper .ant-picker-input input:focus {
                border: none !important;
                box-shadow: none !important;
                outline: none !important;
              }
              .custom-datepicker-wrapper .ant-picker-suffix {
                color: #ffffff !important;
              }
              .custom-datepicker-wrapper .ant-picker-suffix .anticon {
                color: #ffffff !important;
              }
              .custom-datepicker-wrapper .ant-picker-suffix .anticon-calendar {
                color: #ffffff !important;
              }
            `
          }} />
        </div>
      </div>
    );
  };

  render() {
    const { handleSubmit, references, user } = this.props;
    const { completePercent } = this.state;
    
    // Get user type to determine which fields to show
    const userType = user?.mvpUserType || user?.type || '';
    const isExplorer = userType === 'explorer' || userType === 'both' || userType === 'senior';
    const isGuide = userType === 'guide' || userType === 'both' || userType === 'buddy';
    
    // Get category options (level 1 only for favouriteTopics, level 2 for sharingTopics)
    const categoryOptions = this.getAllCategoryOptions();
    const sharingCategoryOptions = this.getSharingCategoryOptions();
    
    return (
      <div className="px-0 user-profile">
        <h3 className="title mt-3 mb-0" style={{ color: '#FFFFFF' }}>{I18n.t('edit_profile_info')}</h3>
        <form className="col-12 mt-4 p0" onSubmit={handleSubmit(this.onBtnUpdateClick)}>
          <div style={{ color: '#FFFFFF', backgroundColor: '#282F39', borderRadius: '12px', padding: '32px' }}>
            <div className="row">
              <Field disabled={false} name="firstName" label={I18n.t('senior_account_account_info_fullname_label')} component={this.renderInput} required={true} />
              <Field disabled={false} name="dateOfBirth" label={I18n.t('date_of_birth')} component={this.renderDatePicker} required={true} />
            </div>

            <div className="row">
              <Field disabled label={I18n.t('senior_account_account_info_email_label')} name="email" component={this.renderInput} required={true} />
              <Field disabled={false} name="phoneNumber" label={I18n.t('phone_number')} component={this.renderInput} />
            </div>

            <div className="row">
              {isExplorer && (
                <Field 
                  disabled={false} 
                  name="favouriteTopics" 
                  label={I18n.t('favourite_topic') || 'Favourite Topic'} 
                  component={this.renderMultiSelect} 
                  required={true}
                  col={isGuide ? 6 : 12}
                  options={categoryOptions}
                />
              )}
              {isGuide && (
                <Field 
                  disabled={false} 
                  name="sharingTopics" 
                  label={I18n.t('sharing_topic') || 'Sharing Topic'} 
                  component={this.renderMultiSelect} 
                  required={true}
                  col={isExplorer ? 6 : 12}
                  options={sharingCategoryOptions}
                />
              )}
            </div>
          </div>

          {/* Delete Account Section */}
          <div style={{
            marginTop: '32px',
            padding: '24px',
            backgroundColor: '#282F39',
            borderRadius: '12px',
            border: '1px solid #374151'
          }}>
            <style>{`
              .delete-account-section {
                display: flex;
                align-items: center;
              }
              .delete-account-content {
                flex: 0 0 80%;
                padding: 0 12px;
              }
              .delete-account-button-wrapper {
                flex: 0 0 20%;
                padding: 0 12px;
                display: flex;
                justify-content: center;
                align-items: center;
              }
              @media (max-width: 767.98px) {
                .delete-account-section {
                  flex-direction: column;
                  align-items: stretch;
                }
                .delete-account-content {
                  flex: 0 0 100%;
                  margin-bottom: 16px;
                }
                .delete-account-button-wrapper {
                  flex: 0 0 100%;
                  justify-content: center;
                }
              }
            `}</style>
            <div className="row delete-account-section" style={{ margin: 0 }}>
              <div className="col-12 col-md-10 col-lg-10 delete-account-content">
                <h4 style={{ color: '#FFFFFF', fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
                  {I18n.t('delete_account_section_title')}
                </h4>
                <p style={{ color: '#9CA3AF', fontSize: '14px', lineHeight: '1.5', margin: 0 }}>
                  {I18n.t('delete_account_section_description')}
                </p>
              </div>
              <div className="col-12 col-md-2 col-lg-2 delete-account-button-wrapper">
                <button
                  type="button"
                  onClick={this.handleDeleteAccountClick}
                  style={{
                    backgroundColor: '#FF445D',
                    color: '#FFFFFF',
                    border: 'none',
                    borderRadius: '16px',
                    padding: '12px 24px',
                    fontSize: '16px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    transition: 'all 0.2s ease',
                    width: '100%',
                    justifyContent: 'center'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.backgroundColor = '#B91C1C';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.backgroundColor = '#FF445D';
                  }}
                >
                  <FontAwesomeIcon icon={faTrashAlt} style={{ fontSize: '16px' }} />
                  <span>{I18n.t('delete')}</span>
                </button>
              </div>
            </div>
          </div>

          <div>
            <button
              type="submit"
              className="btn btn-primary"
              style={{ marginTop: 30, padding: '16px 38px', fontSize: '16px', borderRadius: '16px'}}
              disabled={!!this.props.updating}
            >
              {I18n.t('save') || 'Lưu'}
            </button>
          </div>
        </form>

        {/* Delete Account Confirmation Modal */}
        <style>{`
          .delete-account-modal .modal-content {
            background-color: transparent;
            border: none;
            box-shadow: none;
          }
        `}</style>
        <Modal
          className="delete-account-modal"
          visible={this.state.showDeleteModal}
          dialogClassName="modal-dialog-centered"
          onClickBackdrop={this.closeDeleteModal}
        >
          <div className="modal-body" style={{
            padding: '40px',
            textAlign: 'center',
            backgroundColor: '#1F2937',
            borderRadius: '12px'
          }}>
            {/* Image */}
            <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'center' }}>
              <img 
                src={forgetPasswordImage} 
                alt="Delete account" 
                style={{ maxWidth: '200px', height: 'auto' }}
              />
            </div>

            {/* Title */}
            <h4 style={{ 
              color: '#FFFFFF', 
              fontSize: '20px', 
              fontWeight: '600', 
              marginBottom: '16px',
              lineHeight: '1.4'
            }}>
              {I18n.t('account_request_deletion_title')}
            </h4>

            {/* Description */}
            <p style={{ 
              color: '#9CA3AF', 
              fontSize: '16px', 
              lineHeight: '1.5', 
              marginBottom: '32px',
              padding: '0 20px'
            }}>
              {I18n.t('account_request_deletion_des')}
            </p>

            {/* Buttons */}
            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'center',
              flexWrap: 'wrap'
            }}>
              {/* Maybe Later Button */}
              <button
                type="button"
                onClick={this.closeDeleteModal}
                style={{
                  backgroundColor: 'transparent',
                  color: '#FF445D',
                  border: '1px solid #FF445D',
                  borderRadius: '8px',
                  padding: '12px 32px',
                  fontSize: '16px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  minWidth: '140px'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(255, 68, 93, 0.1)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                {I18n.t('maybe_later')}
              </button>

              {/* Agree Button */}
              <button
                type="button"
                onClick={this.confirmDeleteAccount}
                disabled={this.state.isDeletingAccount}
                style={{
                  backgroundColor: this.state.isDeletingAccount ? '#9CA3AF' : '#FF445D',
                  color: '#FFFFFF',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '12px 32px',
                  fontSize: '16px',
                  fontWeight: '500',
                  cursor: this.state.isDeletingAccount ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease',
                  minWidth: '140px',
                  opacity: this.state.isDeletingAccount ? 0.6 : 1
                }}
                onMouseOver={(e) => {
                  if (!this.state.isDeletingAccount) {
                    e.currentTarget.style.backgroundColor = '#DC2626';
                  }
                }}
                onMouseOut={(e) => {
                  if (!this.state.isDeletingAccount) {
                    e.currentTarget.style.backgroundColor = '#FF445D';
                  }
                }}
              >
                {this.state.isDeletingAccount ? 'Processing...' : I18n.t('agree')}
              </button>
            </div>
          </div>
        </Modal>
      </div>
    );
  }
}

const mapStateToProps = (state) => {
  const { user } = state;
  
  // Xử lý dateOfBirth để đảm bảo format đúng
  let dateOfBirth = '';
  if (user.dateOfBirth) {
    // Nếu dateOfBirth là string, giữ nguyên
    if (typeof user.dateOfBirth === 'string') {
      dateOfBirth = user.dateOfBirth;
    } 
    // Nếu dateOfBirth là Date object, chuyển thành string
    else if (user.dateOfBirth instanceof Date) {
      dateOfBirth = moment(user.dateOfBirth).format('YYYY-MM-DD');
    }
    // Nếu dateOfBirth là moment object, chuyển thành string
    else if (moment.isMoment(user.dateOfBirth)) {
      dateOfBirth = user.dateOfBirth.format('YYYY-MM-DD');
    }
    
  }
  
  // Xử lý phoneNumber để đảm bảo là string
  let phoneNumber = '';
  if (user.phoneNumber) {
    // Nếu phoneNumber là object (có thể có dialCode và number)
    if (typeof user.phoneNumber === 'object' && user.phoneNumber !== null) {
      if (user.phoneNumber.number) {
        // Nếu có dialCode, kết hợp dialCode + number
        phoneNumber = user.phoneNumber.dialCode ? `${user.phoneNumber.dialCode}${user.phoneNumber.number}` : user.phoneNumber.number;
      } else {
        // Nếu là object nhưng không có number, chuyển thành string rỗng
        phoneNumber = '';
      }
    }
    // Nếu phoneNumber là string, giữ nguyên
    else if (typeof user.phoneNumber === 'string') {
      phoneNumber = user.phoneNumber;
    }
    // Các trường hợp khác, chuyển thành string
    else {
      phoneNumber = String(user.phoneNumber);
    }
  }
  
  // Format favouriteTopics for initial values
  // Always ensure it's an array, even if empty
  let favouriteTopics = [];
  if (user.favouriteTopics && Array.isArray(user.favouriteTopics) && user.favouriteTopics.length > 0) {
    // This will be populated after categories are loaded in component
    // For now, just store the IDs
    favouriteTopics = user.favouriteTopics;
  }
  
  // Format sharingTopics for initial values
  // Always ensure it's an array, even if empty
  let sharingTopics = [];
  if (user.sharingTopics && Array.isArray(user.sharingTopics) && user.sharingTopics.length > 0) {
    // This will be populated after categories are loaded in component
    // For now, just store the IDs
    sharingTopics = user.sharingTopics;
  }
  // If user.favouriteTopics or user.sharingTopics is not an array or is empty, use empty array
  // This ensures the select component can work immediately

  // Simple initialValues without complex logic
  const initialValues = {
    _id: user._id || '',
    firstName: user.firstName || user.name || '',
    dateOfBirth: dateOfBirth,
    phoneNumber: phoneNumber,
    walletAddress: user.walletAddress || '',
    email: user.email || '',
    favouriteTopics: favouriteTopics, // Always an array (empty or with values)
    sharingTopics: sharingTopics, // Always an array (empty or with values)
  };
  
  return {
    user,
    updating: user.updating,
    references: user.references,
    initialValues,
    submitErrors: getFormSubmitErrors(FORM_NAME)(state),
  };
};

const mapDispatchToProps = (dispatch) => ({
  updateUserInfo: (options, callback) => dispatch(updateUserInfo(options, callback)),
  fetchAllReferences: () => dispatch(fetchAllReferences()),
  checkPublicUsername: (username) => dispatch(checkPublicUsername(username)),
  fetchUserMvp: (callback) => dispatch(fetchUserMvp(callback)),
  getUserProfileMvp: () => dispatch(getUserProfileMvp()),
  getDaoDetailByCode: (code) => dispatch(getDaoDetailByCode(code)),
  logOutMvp: (accessToken) => dispatch(logOutMvp(accessToken)),
  getCategorySessions: () => dispatch(getCategorySessions()),
  dispatch,
});

// Validate function for redux-form (real-time validation)
const validate = (values, props) => {
  const errors = {};
  const user = props?.user || {};
  
  // Validate firstName (required)
  if (!values.firstName || !values.firstName.trim().length) {
    errors.firstName = I18n.t('text_field_required_error');
  }

  // Validate email (required)
  if (!values.email || !values.email.trim().length) {
    errors.email = I18n.t('text_field_required_error');
  } else {
    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(values.email.trim())) {
      errors.email = I18n.t('invalid_email_format') || 'Email không hợp lệ';
    }
  }

  // Validate dateOfBirth (required when updating profile after login)
  // This form is only used for profile editing after login, so dateOfBirth is always required here
  if (!values.dateOfBirth || !values.dateOfBirth.trim().length) {
    errors.dateOfBirth = I18n.t('text_field_required_error');
  } else {
    // Validate date format
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (!dateRegex.test(values.dateOfBirth.trim())) {
      // If format is not YYYY-MM-DD, try to parse it
      const parsedDate = moment(values.dateOfBirth, ['YYYY-MM-DD', 'DD/MM/YYYY', 'MM/DD/YYYY', 'YYYY/MM/DD'], true);
      if (!parsedDate.isValid()) {
        errors.dateOfBirth = I18n.t('invalid_date_format') || 'Ngày sinh không hợp lệ';
      }
    }
  }

  // Get user type to determine which fields are required
  const userType = user?.mvpUserType || user?.type || '';
  const isExplorer = userType === 'explorer' || userType === 'both' || userType === 'senior';
  const isGuide = userType === 'guide' || userType === 'both' || userType === 'buddy';

  // Validate favouriteTopics (required for explorer or both)
  if (isExplorer) {
    if (!values.favouriteTopics || !Array.isArray(values.favouriteTopics) || values.favouriteTopics.length === 0) {
      errors.favouriteTopics = I18n.t('text_field_required_error');
    } else {
      // Check if only default option is selected (value is empty string)
      const validTopics = values.favouriteTopics.filter(topic => topic && topic.value && topic.value !== '');
      if (validTopics.length === 0) {
        errors.favouriteTopics = I18n.t('text_field_required_error');
      }
    }
  }

  // Validate sharingTopics (required for guide or both)
  if (isGuide) {
    if (!values.sharingTopics || !Array.isArray(values.sharingTopics) || values.sharingTopics.length === 0) {
      errors.sharingTopics = I18n.t('text_field_required_error');
    } else {
      // Check if only default option is selected (value is empty string)
      const validTopics = values.sharingTopics.filter(topic => topic && topic.value && topic.value !== '');
      if (validTopics.length === 0) {
        errors.sharingTopics = I18n.t('text_field_required_error');
      }
    }
  }

  return errors;
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(
  reduxForm({
    form: FORM_NAME,
    enableReinitialize: true,
    keepDirtyOnReinitialize: false,
    validate,
  })(Profile),
);
